import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { PageHeader, Surface, Empty } from "../components/Primitives";
import { Camera, Pause, Play, ScanLine, Plus, Check } from "lucide-react";
import { toast } from "sonner";

let Html5QrCode = null;
async function loadScanner() {
  if (!Html5QrCode) {
    const mod = await import("html5-qrcode");
    Html5QrCode = mod.Html5Qrcode;
  }
  return Html5QrCode;
}

export default function Scanner() {
  const containerRef = useRef(null);
  const scannerRef = useRef(null);
  const [running, setRunning] = useState(false);
  const [products, setProducts] = useState([]);
  const [last, setLast] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newProduct, setNewProduct] = useState({ name: "", sku: "", price: 0, cost: 0, min_stock: 10, unit: "boîte" });

  const loadProducts = async () => {
    const { data } = await api.get("/products");
    setProducts(data);
  };

  useEffect(() => {
    loadProducts();
    return () => { stop(); /* eslint-disable-next-line */ };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const findProduct = (code) => {
    const c = (code || "").trim();
    return products.find((p) => p.barcode === c) || products.find((p) => p.sku === c);
  };

  const onDecoded = (decodedText) => {
    const found = findProduct(decodedText);
    setLast({ code: decodedText, product: found || null, ts: new Date().toISOString() });
    if (found) toast.success(`Produit trouvé: ${found.name}`);
    else toast.warning(`Code inconnu: ${decodedText}`);
  };

  const start = async () => {
    try {
      const Lib = await loadScanner();
      const id = "scanner-frame";
      if (!scannerRef.current) {
        scannerRef.current = new Lib(id);
      }
      await scannerRef.current.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 260, height: 160 } },
        onDecoded,
        () => {}
      );
      setRunning(true);
    } catch (e) {
      toast.error("Impossible d'accéder à la caméra. Donnez l'autorisation et utilisez HTTPS / l'app installée.");
    }
  };

  const stop = async () => {
    try {
      if (scannerRef.current && running) {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
      }
    } catch {}
    setRunning(false);
  };

  const openCreate = () => {
    if (!last) return;
    setNewProduct({
      name: "",
      sku: last.code,
      price: 0,
      cost: 0,
      min_stock: 10,
      unit: "boîte",
    });
    setShowCreate(true);
  };

  const saveNewProduct = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: newProduct.name,
        sku: newProduct.sku,
        barcode: last.code,
        category: "",
        unit: newProduct.unit,
        price: Number(newProduct.price || 0),
        cost: Number(newProduct.cost || 0),
        min_stock: Number(newProduct.min_stock || 10),
      };
      await api.post("/products", payload);
      toast.success(`Produit "${newProduct.name}" créé avec le code-barres ${last.code}`);
      setShowCreate(false);
      await loadProducts();
      // Re-evaluate last scan
      setLast({ ...last, product: { ...payload, id: "new" } });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Scanner code-barres" subtitle="Caméra mobile · Reconnaissance instantanée"
        actions={
          running
            ? <button data-testid="stop-scan" className="btn-ghost flex items-center gap-2" onClick={stop}><Pause size={14}/>Arrêter</button>
            : <button data-testid="start-scan" className="btn-primary flex items-center gap-2" onClick={start}><Play size={14}/>Démarrer la caméra</button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Surface title="Caméra">
          <div className="p-4">
            <div ref={containerRef} className="relative bg-black/60 rounded-sm border border-[var(--border-line)] overflow-hidden" style={{ minHeight: 320 }}>
              <div id="scanner-frame" data-testid="scanner-frame" className="w-full" />
              {!running && (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-[var(--text-muted)] gap-2">
                  <ScanLine size={48} className="text-[var(--primary)] opacity-50"/>
                  <div className="font-data text-xs uppercase tracking-wider">Caméra inactive</div>
                  <div className="text-xs">Cliquez "Démarrer la caméra" et autorisez l'accès</div>
                </div>
              )}
            </div>
            <div className="text-xs text-[var(--text-muted)] mt-3 font-data">
              <Camera size={12} className="inline mr-1"/> Formats supportés : EAN-13 / EAN-8 / Code 128 / DataMatrix / QR
            </div>
          </div>
        </Surface>

        <Surface title="Dernier scan" testId="last-scan">
          {!last ? <Empty message="Aucun scan pour le moment"/> : (
            <div className="p-5 space-y-3">
              <div>
                <div className="label-mono">Code détecté</div>
                <div className="font-data text-2xl text-[var(--primary)] tracking-tight break-all">{last.code}</div>
              </div>
              {last.product ? (
                <div className="surface p-4 space-y-2">
                  <div className="badge-success w-fit flex items-center gap-1"><Check size={10}/>PRODUIT TROUVÉ</div>
                  <div className="font-heading text-xl">{last.product.name}</div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div><div className="label-mono">SKU</div><div className="font-data">{last.product.sku}</div></div>
                    <div><div className="label-mono">Prix</div><div className="font-data text-[var(--primary-dark)] font-semibold">{last.product.price.toLocaleString("fr-FR")} F</div></div>
                    <div><div className="label-mono">Stock min</div><div className="font-data">{last.product.min_stock}</div></div>
                  </div>
                </div>
              ) : (
                <div className="surface p-4 space-y-3 bg-[var(--warn-soft)] border-[#FCD34D]">
                  <div className="badge-warn w-fit">CODE INCONNU</div>
                  <div className="text-sm text-[var(--text)]">
                    Ce code-barres n'est pas encore associé à un produit. Voulez-vous l'ajouter maintenant ?
                  </div>
                  <button data-testid="create-product-from-scan" onClick={openCreate} className="btn-primary flex items-center gap-2">
                    <Plus size={14}/>Créer ce produit
                  </button>
                </div>
              )}
            </div>
          )}
        </Surface>
      </div>

      {showCreate && last && (
        <Surface title={`Nouveau produit · code ${last.code}`} action={<button onClick={()=>setShowCreate(false)} className="btn-ghost text-xs">Annuler</button>}>
          <form onSubmit={saveNewProduct} className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="sm:col-span-2 lg:col-span-2">
              <label className="label-mono block mb-1.5">Nom du médicament</label>
              <input data-testid="quickadd-name" required autoFocus className="input-base" placeholder="Ex: Doliprane 500mg" value={newProduct.name} onChange={(e)=>setNewProduct({...newProduct, name: e.target.value})}/>
            </div>
            <div>
              <label className="label-mono block mb-1.5">SKU interne</label>
              <input data-testid="quickadd-sku" required className="input-base font-data" value={newProduct.sku} onChange={(e)=>setNewProduct({...newProduct, sku: e.target.value})}/>
            </div>
            <div>
              <label className="label-mono block mb-1.5">Code-barres</label>
              <input className="input-base font-data" value={last.code} disabled/>
            </div>
            <div>
              <label className="label-mono block mb-1.5">Unité</label>
              <select className="input-base" value={newProduct.unit} onChange={(e)=>setNewProduct({...newProduct, unit: e.target.value})}>
                <option>boîte</option><option>blister</option><option>flacon</option><option>tube</option><option>sachet</option><option>ampoule</option><option>unité</option>
              </select>
            </div>
            <div>
              <label className="label-mono block mb-1.5">Stock minimum</label>
              <input type="number" min="0" className="input-base" value={newProduct.min_stock} onChange={(e)=>setNewProduct({...newProduct, min_stock: e.target.value})}/>
            </div>
            <div>
              <label className="label-mono block mb-1.5">Prix vente (F)</label>
              <input data-testid="quickadd-price" type="number" min="0" step="1" required className="input-base" value={newProduct.price} onChange={(e)=>setNewProduct({...newProduct, price: e.target.value})}/>
            </div>
            <div>
              <label className="label-mono block mb-1.5">Coût d'achat (F)</label>
              <input type="number" min="0" step="1" className="input-base" value={newProduct.cost} onChange={(e)=>setNewProduct({...newProduct, cost: e.target.value})}/>
            </div>
            <div className="sm:col-span-2 lg:col-span-3 flex gap-2">
              <button data-testid="quickadd-submit" type="submit" className="btn-primary flex items-center gap-2"><Check size={14}/>Enregistrer le produit</button>
              <button type="button" onClick={()=>setShowCreate(false)} className="btn-ghost">Annuler</button>
            </div>
          </form>
        </Surface>
      )}
    </div>
  );
}
